import { useState } from "react";
import { Plus, X, Copy, ClipboardPaste } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { RowControls } from "@/components/course/LessonEditor";

const blank = (type) => ({ type, prompt: "", options: type === "tf" ? ["True", "False"] : ["", "", "", ""], correct_index: 0 });
const EXAMPLE = "Q: When is lot rent due?\nA) The 15th\n*B) The 1st\nC) The last day\n\nQ: Payment plans must be signed by the resident.\n*True\nFalse";

export function parseBulk(text) {
  const out = [], errors = [];
  let cur = null;
  const flush = () => {
    if (!cur) return;
    const correct = cur.opts.findIndex((o) => o.correct);
    const opts = cur.opts.map((o) => o.text);
    const tf = opts.length === 2 && /^true$/i.test(opts[0]) && /^false$/i.test(opts[1]);
    if (opts.length < 2) errors.push(`“${cur.prompt.slice(0, 40)}” needs at least 2 options`);
    else if (correct < 0) errors.push(`“${cur.prompt.slice(0, 40)}” has no correct answer (mark it with *)`);
    else out.push({ type: tf ? "tf" : "mc", prompt: cur.prompt, options: tf ? ["True", "False"] : opts, correct_index: correct });
    cur = null;
  };
  for (const raw of text.split(/\r?\n/)) {
    const line = raw.trim();
    if (!line) continue;
    const q = line.match(/^Q\d*[:.)]\s*(.+)$/i);
    if (q) { flush(); cur = { prompt: q[1], opts: [] }; continue; }
    const o = line.match(/^(\*?)\s*(?:[A-Ha-h][).]\s*)?(.+)$/);
    if (!cur) { errors.push(`Line “${line.slice(0, 40)}” is outside a question (start questions with Q:)`); continue; }
    cur.opts.push({ correct: !!o[1], text: o[2].trim() });
  }
  flush();
  return { questions: out, errors };
}

function BulkAdd({ onAdd }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const { questions, errors } = parseBulk(text);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-lasso-line px-4 py-2 text-xs font-semibold hover:border-lasso-ink" data-testid="builder-bulk-add-button"><ClipboardPaste className="h-3.5 w-3.5" /> Bulk add questions</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[88vh] max-w-3xl overflow-y-auto" data-testid="bulk-add-dialog">
          <DialogHeader><DialogTitle className="font-display">Bulk add questions</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">One question per block. Start with <b>Q:</b>, then one option per line (A), B)… optional). Put <b>*</b> before the correct answer. Options “True” and “False” make a True/False question.</p>
          <pre className="rounded-xl bg-lasso-tint p-3 text-xs">{EXAMPLE}</pre>
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={10} className="w-full rounded-xl border border-lasso-line p-3 font-mono text-sm" placeholder="Paste questions here" data-testid="bulk-add-textarea" />
          {errors.map((e, i) => <p key={i} className="text-xs font-semibold text-rose-600" data-testid="bulk-add-error">{e}</p>)}
          {questions.length > 0 && (
            <div className="space-y-2" data-testid="bulk-add-preview">
              <p className="text-sm font-semibold">Preview · {questions.length} question{questions.length > 1 ? "s" : ""}</p>
              {questions.map((q, i) => (
                <div key={i} className="rounded-xl border border-lasso-line p-3 text-sm">
                  <p className="font-medium">{i + 1}. {q.prompt} <span className="text-[10px] uppercase text-zinc-400">{q.type === "tf" ? "True/False" : "Multiple choice"}</span></p>
                  <ul className="mt-1 text-xs">{q.options.map((o, j) => <li key={j} className={j === q.correct_index ? "font-semibold text-emerald-700" : "text-zinc-600"}>{j === q.correct_index ? "✓ " : "· "}{o}</li>)}</ul>
                </div>
              ))}
            </div>
          )}
          <button type="button" disabled={!questions.length || errors.length > 0} onClick={() => { onAdd(questions); setText(""); setOpen(false); }}
            className="rounded-full bg-lasso-ink px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-40" data-testid="bulk-add-confirm">Add {questions.length || ""} to bank</button>
        </DialogContent>
      </Dialog>
    </>
  );
}

function QuestionEditor({ q, index, total, onChange, onMove, onRemove, onDuplicate }) {
  const t = `builder-question-${index + 1}`;
  const removeOption = (oi) => {
    const ci = q.correct_index;
    onChange({ ...q, options: q.options.filter((_, j) => j !== oi), correct_index: oi < ci ? ci - 1 : oi === ci ? 0 : ci });
  };
  return (
    <div id={q.id ? `q-${q.id}` : undefined} className="scroll-mt-24 rounded-2xl border border-lasso-line bg-white p-5" data-testid={t}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wider text-lasso-deep">Q{index + 1} · {q.type === "tf" ? "True / False" : "Multiple choice"}</span>
        <div className="flex items-center gap-1">
          <button type="button" onClick={onDuplicate} className="rounded-md p-2 text-zinc-400 hover:bg-lasso-tint hover:text-lasso-ink" data-testid={`${t}-duplicate`} aria-label="Duplicate"><Copy className="h-3.5 w-3.5" /></button>
          <RowControls index={index} total={total} onMove={onMove} onRemove={onRemove} testId={t} />
        </div>
      </div>
      <Input className="mt-3" placeholder="Question" value={q.prompt} onChange={(e) => onChange({ ...q, prompt: e.target.value })} data-testid={`${t}-prompt`} />
      <div className="mt-3 space-y-2">
        {q.options.map((o, oi) => (
          <div key={oi} className="flex items-center gap-3">
            <input type="radio" name={`correct-${index}`} className="accent-black" checked={q.correct_index === oi} onChange={() => onChange({ ...q, correct_index: oi })} data-testid={`${t}-correct-${oi}`} aria-label="Correct answer" />
            {q.type === "tf" ? <span className="text-sm">{o}</span> : (
              <>
                <Input value={o} placeholder={`Option ${oi + 1}`} onChange={(e) => onChange({ ...q, options: q.options.map((x, j) => (j === oi ? e.target.value : x)) })} data-testid={`${t}-option-${oi}`} />
                <button type="button" disabled={q.options.length <= 2} onClick={() => removeOption(oi)} className="rounded-md p-2 text-zinc-400 hover:bg-rose-50 hover:text-rose-600 disabled:opacity-30" data-testid={`${t}-remove-option-${oi}`} aria-label="Remove option"><X className="h-3.5 w-3.5" /></button>
              </>
            )}
          </div>
        ))}
        {q.type === "mc" && q.options.length < 8 && (
          <button type="button" onClick={() => onChange({ ...q, options: [...q.options, ""] })} className="ml-7 inline-flex items-center gap-1 text-xs font-semibold text-lasso-deep hover:underline" data-testid={`${t}-add-option`}><Plus className="h-3 w-3" /> Add option</button>
        )}
      </div>
    </div>
  );
}

export function QuizEditor({ quiz, onChange }) {
  const qs = quiz.questions;
  const setQs = (questions) => onChange({ ...quiz, questions });
  const move = (i, d) => {
    const copy = [...qs];
    [copy[i], copy[i + d]] = [copy[i + d], copy[i]];
    setQs(copy);
  };
  const per = quiz.per_attempt || "";
  const tooMany = quiz.per_attempt && quiz.per_attempt > qs.length;
  return (
    <div className="space-y-4">
      <div className="grid gap-4 rounded-2xl border border-lasso-line bg-lasso-paper p-4 md:grid-cols-4" data-testid="builder-exam-settings">
        <div className="space-y-2">
          <Label>Pass mark (%)</Label>
          <Input type="number" min={1} max={100} value={quiz.pass_mark} onChange={(e) => onChange({ ...quiz, pass_mark: Number(e.target.value) || 80 })} data-testid="builder-quiz-pass-mark" />
        </div>
        <div className="space-y-2">
          <Label>Questions per attempt</Label>
          <Input type="number" min={1} placeholder={`All (${qs.length})`} value={per} onChange={(e) => onChange({ ...quiz, per_attempt: e.target.value ? Number(e.target.value) : null })} data-testid="builder-quiz-per-attempt" />
        </div>
        <label className="flex items-center gap-2 text-sm md:pt-7"><Switch checked={quiz.shuffle_questions !== false} onCheckedChange={(v) => onChange({ ...quiz, shuffle_questions: v })} data-testid="builder-quiz-shuffle-questions" /> Shuffle questions</label>
        <label className="flex items-center gap-2 text-sm md:pt-7"><Switch checked={quiz.shuffle_options !== false} onCheckedChange={(v) => onChange({ ...quiz, shuffle_options: v })} data-testid="builder-quiz-shuffle-options" /> Shuffle answers</label>
        <p className={`text-xs md:col-span-4 ${tooMany ? "font-semibold text-rose-600" : "text-muted-foreground"}`} data-testid="builder-bank-count">
          Question bank: {qs.length} question{qs.length === 1 ? "" : "s"} · each attempt draws {Math.min(quiz.per_attempt || qs.length, qs.length)} at random{tooMany ? ` · questions per attempt can't exceed the bank size (${qs.length})` : ""}. Unlocks after all lessons; unlimited retakes.
        </p>
      </div>
      {qs.map((q, i) => (
        <QuestionEditor key={q.id || `n${i}`} q={q} index={i} total={qs.length} onChange={(v) => setQs(qs.map((x, j) => (j === i ? v : x)))} onMove={(d) => move(i, d)}
          onRemove={() => setQs(qs.filter((_, j) => j !== i))} onDuplicate={() => setQs([...qs.slice(0, i + 1), { ...q, id: undefined, options: [...q.options], prompt: `${q.prompt} (copy)` }, ...qs.slice(i + 1)])} />
      ))}
      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={() => setQs([...qs, blank("mc")])} className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-lasso-line px-4 py-2 text-xs font-semibold hover:border-lasso-ink" data-testid="builder-add-mc-question"><Plus className="h-3.5 w-3.5" /> Multiple choice</button>
        <button type="button" onClick={() => setQs([...qs, blank("tf")])} className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-lasso-line px-4 py-2 text-xs font-semibold hover:border-lasso-ink" data-testid="builder-add-tf-question"><Plus className="h-3.5 w-3.5" /> True / False</button>
        <BulkAdd onAdd={(n) => setQs([...qs, ...n])} />
      </div>
    </div>
  );
}
